import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import styles from "./style.module.scss";
import cxStyles from "components/Alerts/cxAlert.module.scss";
import { Select, notification, Spin, Button } from "antd";
import SortPopup from "helperComponents/SortPopup";
import { useSelector } from "react-redux";
import AlertPopup from "components/GlobalExecutiveDashboard/Alert";
import AlertSeverityBadge from "components/Alerts/AlertSeverityBadge";
import AlertHistoryModal from "components/Alerts/AlertHistoryModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "util/services";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { copyToClipboard } from "util/common";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  noticeAlertsQueryKeys,
  fetchNoticeAlerts,
} from "queries/noticeAlerts";
import AlertLimitUpgradeModal from "components/Alerts/AlertLimitUpgradeModal";
import { useHidePlatformPricing } from "util/platformPricing";
import {
  formatAbsoluteDate,
  formatFrequencyBadge,
  formatFireCount30d,
  formatAlertTeamLabel,
  getMonitoredMetricChips,
  normalizeAlertList,
} from "util/cxAlertModel";
const { Option } = Select;

const Alerts = ({ embedded = false, user, theme: themeProp }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const uid = user?.uid;

  const [searchValue, setSearchValue] = useState("");
  const [committedKeyword, setCommittedKeyword] = useState("");
  const [sortValue, setSortValue] = useState("newest");
  const [showRowItems, setShowRowItems] = useState(true);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [showSortPopup, setShowSortPopup] = useState(false);
  const [conditionFilter] = useState("all");
  const [startIndex, setStartIndex] = useState(0);
  const [alertsPageNumber, setAlertsPageNumber] = useState(1);
  const [selectedPerPage, setSelectedPerPage] = useState(10);
  const [numOfAlerts, setNumOfAlerts] = useState(10);
  const [disableBtn, setDisableBtn] = useState(false);
  const [showMoreOption, setShowMoreOption] = useState(false);
  const [showEditAlert, setShowEditAlert] = useState(false);
  const [showAlertInfoPopup, setShowAlertInfoPopup] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedAlertID, setSelectedAlertID] = useState("");
  const [selectedAlertUsers, setSelectedAlertUsers] = useState([]);
  const [sendSMSCheckbox, setSendSMSCheckbox] = useState(false);
  const [selectedAlertCondition, setSelectedAlertCondition] = useState("");
  const [selectedAlertThreshold, setSelectedAlertThreshold] = useState(null);
  const [selectedAlertFrequency, setSelectedAlertFrequency] = useState("");
  const [alertInfo, setAlertInfo] = useState(null);
  const [historyRecord, setHistoryRecord] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const moreOptionRef = useRef();
  const history = useHistory();
  const location = useLocation();
  const deepLinkHandledRef = useRef(false);

  const themeFromStore = useSelector((state) => state.DashboardReducer.theme);
  const theme = themeProp ?? themeFromStore;
  const hidePricing = useHidePlatformPricing();

  const listParams = useMemo(
    () => ({
      startIndex,
      numPerPage: numOfAlerts,
      sort: sortValue,
      condition: conditionFilter,
      keyword: committedKeyword,
    }),
    [startIndex, numOfAlerts, sortValue, conditionFilter, committedKeyword],
  );

  const listQueryKey = useMemo(
    () => noticeAlertsQueryKeys.list(uid, listParams),
    [uid, listParams],
  );

  const alertsQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => fetchNoticeAlerts(listParams),
    enabled: Boolean(uid),
    keepPreviousData: true,
    onError: (err) =>
      notification.error({
        message:
          typeof err?.message === "string"
            ? err.message
            : t("notification_page.1"),
      }),
  });

  const alertsList = normalizeAlertList(alertsQuery.data?.alerts ?? []);
  const totalAlerts = alertsQuery.data?.totalCount ?? 0;
  const alertLimits = alertsQuery.data?.limits ?? null;
  const alertsFetching = alertsQuery.isFetching;

  const invalidateAlertsQueries = useCallback(async () => {
    if (!uid) return;
    await queryClient.invalidateQueries({
      queryKey: noticeAlertsQueryKeys.all(uid),
    });
  }, [queryClient, uid]);

  const submitSearch = useCallback(() => {
    setCommittedKeyword(searchValue);
    setStartIndex(0);
    setAlertsPageNumber(1);
  }, [searchValue]);

  const clearSearchMobile = useCallback(() => {
    setShowRowItems(true);
    setSearchValue("");
    setCommittedKeyword("");
    setStartIndex(0);
    setAlertsPageNumber(1);
  }, []);

  const handleSearch = (event) => {
    if (event.key === "Enter") {
      submitSearch();
    }
  };

  const handleShowSearch = () => {
    if (window.innerWidth < 880) {
      setShowRowItems(false);
    }
    setShowSearchBox(!showSearchBox);
  };

  const handleShowEditAlert = (record) => {
    const raw = record._raw ?? record;
    setSelectedAlertCondition(raw.condition);
    setSelectedAlertFrequency(raw.frequency);
    setSelectedAlertThreshold(raw.threshold);
    setSelectedAlertUsers(raw.send_to);
    setSendSMSCheckbox(raw.send_sms);
    setShowEditAlert(true);
  };

  const handleShowAlertInfo = (record) => {
    setAlertInfo(record);
    setShowAlertInfoPopup(true);
  };

  const handleShowHistory = (record) => {
    setHistoryRecord(record);
    setShowHistoryModal(true);
    setShowMoreOption(false);
  };

  useEffect(() => {
    deepLinkHandledRef.current = false;
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("alert")) {
      setNumOfAlerts((prev) => (prev < 200 ? 200 : prev));
      setStartIndex(0);
      setAlertsPageNumber(1);
    }
  }, [location.search]);

  useEffect(() => {
    if (deepLinkHandledRef.current || alertsFetching) return;
    const params = new URLSearchParams(location.search);
    const alertId = params.get("alert");
    const openMode = params.get("open");
    if (!alertId) return;

    const record = alertsList.find(
      (item) => String(item.alert_id) === String(alertId)
    );
    if (!record) return;

    if (openMode === "history") {
      handleShowHistory(record);
    } else {
      handleShowAlertInfo(record);
    }
    deepLinkHandledRef.current = true;
    history.replace("/console/alerts");
  }, [alertsFetching, alertsList, location.search, history]);

  const handleShowDeleteAlert = () => {
    setShowDeleteAlert(true);
  };

  const handleUserPage = (e) => {
    if (e.which === 13) {
      if (!alertsPageNumber) {
        notification.info({
          message: t("notifications.2"),
          icon: (
            <InfoCircleOutlined
              style={{ color: theme === "dark" ? "white" : "black" }}
            />
          ),
        });
      } else if (
        alertsPageNumber > 0 &&
        alertsPageNumber <= Math.ceil(totalAlerts / selectedPerPage) &&
        /^\d+$/.test(alertsPageNumber)
      ) {
        setStartIndex(alertsPageNumber - 1);
        e.target.blur();
      } else {
        notification.info({
          message: t("notifications.2"),
          icon: (
            <InfoCircleOutlined
              style={{ color: theme === "dark" ? "white" : "black" }}
            />
          ),
        });
      }
    }
  };

  const handlePageChange = (value) => {
    setSelectedPerPage(value);
    setNumOfAlerts(value);
    setStartIndex(0);
    setAlertsPageNumber(1);
  };

  const optionOutClick = (e) => {
    if (moreOptionRef.current && !moreOptionRef.current.contains(e.target)) {
      setShowMoreOption(false);
    }
  };
  useEffect(() => {
    document.addEventListener("click", optionOutClick, true);
    return () => document.removeEventListener("click", optionOutClick, true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const purchase = params.get("alert_purchase");
    if (!purchase) return;
    if (purchase === "success") {
      notification.success({
        message: t(
          "alerts_page.purchase_success",
          "Ek alarm paketi etkinleştirildi. Yeni alarm oluşturabilirsiniz.",
        ),
      });
      invalidateAlertsQueries();
    } else if (purchase === "cancelled") {
      notification.info({
        message: t("alerts_page.purchase_cancelled", "Ödeme iptal edildi."),
      });
    }
    params.delete("alert_purchase");
    const nextSearch = params.toString();
    history.replace({
      pathname: history.location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  }, [history, t, invalidateAlertsQueries]);

  const handleEditAlert = async (
    condition,
    threshold,
    users,
    frequency,
    smsBoxChecked
  ) => {
    setDisableBtn(true);
    const payload = {
      alert_id: selectedAlertID,
      condition: condition,
      threshold: parseInt(threshold, 10),
      send_to: users,
      frequency: frequency,
      sms_checked: smsBoxChecked,
    };

    const response = await apiRequest(
      "notice/alerts/edit",
      "POST",
      payload,
      true
    );
    if (response.success) {
      notification.success({
        message: t("notifications.145"),
      });
      setDisableBtn(false);
      setShowEditAlert(false);
      await invalidateAlertsQueries();
    } else {
      notification.error({
        message: response.error,
      });
      setDisableBtn(false);
    }
  };

  const handleDeleteAlert = async () => {
    setDisableBtn(true);
    const payload = {
      alert_id: selectedAlertID,
    };
    const response = await apiRequest(
      "notice/alerts/delete",
      "POST",
      payload,
      true
    );
    if (response.success) {
      notification.success({
        message: t("notifications.146"),
      });
      setDisableBtn(false);
      setShowDeleteAlert(false);
      await invalidateAlertsQueries();
    } else {
      notification.error({
        message: response.error,
      });
      setDisableBtn(false);
    }
  };


  const handleCopy = (str) => {
    if (str) {
      copyToClipboard(str);
      notification.success({
        message: t("notifications.54"),
      });
    }
  };

  return (
    <div
      className={`${styles.mainWindow} ${
        embedded ? styles.mainWindowEmbedded : ""
      } ${embedded && theme === "dark" ? styles.mainWindowEmbeddedDark : ""}`}
    >
      <div>
        {!embedded && (
          <div className={styles.headerSection}>
            <p>{t("alerts_page.1")}</p>
            <p>{t("alerts_page.2")}</p>
            {alertLimits && (
              <div className={styles.limitsBanner}>
                <span>
                  {t("alerts_page.usage_banner", "{{used}} / {{max}} alarm kullanılıyor", {
                    used: alertLimits.used,
                    max: alertLimits.max_allowed,
                  })}
                </span>
                {(alertLimits.payment_required || alertLimits.kpi_payment_required) &&
                  alertLimits.can_purchase &&
                  (hidePricing ? (
                    <Button
                      type="link"
                      size="small"
                      href="mailto:hello@pivony.com?subject=Alerts%20%26%20KPI%20capacity%20request"
                    >
                      {t("alerts_page.contact_sales_capacity")}
                    </Button>
                  ) : (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setShowUpgradeModal(true)}
                    >
                      {t(
                        "alerts_page.usage_upgrade_cta",
                        "Add-on satın al (+10 KPI + alarm)",
                      )}
                    </Button>
                  ))}
              </div>
            )}
          </div>
        )}

        {alertsList?.length !== 0 && (
          <div className={styles.container}>
            {showRowItems && (
              <div className={styles.firstRow}>
                <div className={styles.actionBtnBlock}>
                  <button className={styles.search} onClick={handleShowSearch}>
                    <img
                      src={
                        theme === "dark"
                          ? "/assets/search.svg"
                          : "/assets/search_black.svg"
                      }
                      alt=""
                    />
                  </button>
                  <img
                    src={
                      theme === "dark"
                        ? "/assets/sort_icon.svg"
                        : "/assets/sort_icon_black.svg"
                    }
                    alt=""
                    className={styles.sort}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSortPopup(!showSortPopup);
                    }}
                  />
                  {showSortPopup && (
                    <SortPopup
                      setSortValue={setSortValue}
                      sortValue={sortValue}
                      setShowSortPopup={setShowSortPopup}
                    />
                  )}

                  {window.innerWidth > 880 && (
                    <div
                      className={`${styles.searchBox} ${showSearchBox &&
                        styles.showSearch} `}
                    >
                      <img
                        src={
                          theme === "dark"
                            ? "/assets/search.svg"
                            : "/assets/search_black.svg"
                        }
                        alt=""
                      />
                      <input
                        type="text"
                        placeholder={t("alerts_page.3")}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyPress={handleSearch}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {!showRowItems && (
              <div
                className={`${styles.searchBoxSmall} ${showSearchBox &&
                  styles.showSearch} `}
              >
                <i
                  onClick={clearSearchMobile}
                  className="fa-solid fa-arrow-left"
                ></i>
                <input
                  type="text"
                  placeholder={t("alerts_page.3")}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={handleSearch}
                />
                <button type="button" onClick={submitSearch}>{t("button.5")}</button>
              </div>
            )}
            <div className={styles.tableContainer}>
              <div className={`${styles.row} ${styles.header}`}>
                <div className={styles.name}>{t("alerts_page.6")}</div>
                <div className={styles.team}>{t("alerts_page.col_team", "Team")}</div>
                <div className={styles.frequency}>{t("alerts_page.col_frequency")}</div>
                <div className={styles.metrics}>{t("alerts_page.col_metrics")}</div>
                <div className={styles.lastAlarm}>{t("alerts_page.col_last_alarm")}</div>
                <div className={styles.fireCount}>{t("alerts_page.col_fire_count")}</div>
                <div className={styles.status}>{t("alerts_page.col_status")}</div>
                <div className={styles.actions}></div>
              </div>
              <Spin spinning={alertsFetching} style={{ marginTop: "20px" }}>
                {alertsList?.map((record) => {
                  return (
                    <div className={styles.row} key={record.alert_id}>
                      <div className={styles.name}>
                        <div>{record.kpi_name}</div>
                        {record.pivot_subtitle ? (
                          <div className={styles.kpiPivotSubtitle}>
                            {record.pivot_subtitle}
                          </div>
                        ) : null}
                      </div>
                      <div className={styles.team}>
                        <span
                          className={`${cxStyles.teamBadge} ${
                            theme === "dark" ? cxStyles.teamBadgeOnDark : ""
                          }`}
                        >
                          {formatAlertTeamLabel(record, t)}
                        </span>
                      </div>
                      <div className={styles.frequency}>
                        <div className={cxStyles.freqBadges}>
                          {record.frequency.map((freq) => (
                            <span
                              key={freq}
                              className={`${cxStyles.freqBadge} ${
                                theme === "dark" ? cxStyles.freqBadgeOnDark : ""
                              }`}
                            >
                              {formatFrequencyBadge(freq, t)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className={styles.metrics}>
                        <div className={cxStyles.metricChips}>
                          {getMonitoredMetricChips(record).map(({ key, labelKey }) => (
                            <span
                              key={key}
                              className={`${cxStyles.metricChip} ${
                                theme === "dark" ? cxStyles.metricChipOnDark : ""
                              }`}
                            >
                              {labelKey ? t(labelKey) : key}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className={styles.mobileMeta}>
                        <span
                          className={`${cxStyles.teamBadge} ${
                            theme === "dark" ? cxStyles.teamBadgeOnDark : ""
                          }`}
                        >
                          {formatAlertTeamLabel(record, t)}
                        </span>
                        <div className={cxStyles.freqBadges}>
                          {record.frequency.map((freq) => (
                            <span
                              key={freq}
                              className={`${cxStyles.freqBadge} ${
                                theme === "dark" ? cxStyles.freqBadgeOnDark : ""
                              }`}
                            >
                              {formatFrequencyBadge(freq, t)}
                            </span>
                          ))}
                        </div>
                        {record.last_fired_at ? (
                          <AlertSeverityBadge
                            severity={record.last_severity || "warning"}
                          />
                        ) : (
                          <span className={styles.neverFired}>
                            {t("alerts_page.last_alarm_none")}
                          </span>
                        )}
                      </div>
                      <div className={styles.lastAlarm}>
                        <div className={cxStyles.lastAlarmCell}>
                          {record.last_fired_at ? (
                            <>
                              <div>{formatAbsoluteDate(record.last_fired_at)}</div>
                              {record.last_severity && (
                                <AlertSeverityBadge severity={record.last_severity} />
                              )}
                            </>
                          ) : (
                            <span className={styles.neverFired}>
                              {t("alerts_page.last_alarm_none")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles.fireCount}>
                        {record.fire_count_30d > 0 ? (
                          <button
                            type="button"
                            className={cxStyles.fireCountLink}
                            onClick={() => handleShowHistory(record)}
                          >
                            {formatFireCount30d(record.fire_count_30d, t)}
                          </button>
                        ) : (
                          <span className={cxStyles.fireCountZero}>
                            {formatFireCount30d(0, t)}
                          </span>
                        )}
                      </div>
                      <div className={styles.status}>
                        {record.is_active ? (
                          <span className={cxStyles.statusActive}>
                            <span className={cxStyles.statusDot} aria-hidden="true" />
                            {t("alerts_page.status_active")}
                          </span>
                        ) : (
                          <span className={cxStyles.fireCountZero}>
                            {t("alerts_page.status_inactive")}
                          </span>
                        )}
                      </div>
                      <div className={styles.actions}>
                        <img
                          src={
                            theme === "dark"
                              ? "/assets/more-vertical.svg"
                              : "/assets/more-vertical-black.svg"
                          }
                          onClick={() => {
                            setShowMoreOption(true);
                            setSelectedAlertID(record.alert_id);
                          }}
                          alt=""
                        />
                        {showMoreOption && selectedAlertID === record.alert_id && (
                          <div
                            className={styles.moreOptionPopup}
                            ref={moreOptionRef}
                          >
                            <span onClick={() => handleShowHistory(record)}>
                              <img
                                src={
                                  theme === "dark"
                                    ? "/assets/clock.svg"
                                    : "/assets/clock_black.svg"
                                }
                                alt=""
                              />
                              {t("alerts_page.menu_history")}
                            </span>
                            <span onClick={() => handleShowAlertInfo(record)}>
                              <img
                                src={
                                  theme === "dark"
                                    ? "/assets/info1.svg"
                                    : "/assets/info1_black.svg"
                                }
                                alt=""
                              />
                              {t("alerts_page.5")}
                            </span>
                            <span onClick={() => handleShowEditAlert(record)}>
                              <img
                                src={
                                  theme === "dark"
                                    ? "/assets/rename.svg"
                                    : "/assets/rename_black.svg"
                                }
                                alt=""
                              />
                              {t("button.31")}
                            </span>
                            <span onClick={() => handleShowDeleteAlert()}>
                              <img
                                src={
                                  theme === "dark"
                                    ? "/assets/trash.svg"
                                    : "/assets/trash_black.svg"
                                }
                                alt=""
                              />
                              {t("button.2")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Spin>
            </div>
          </div>
        )}
        {!alertsFetching &&
          alertsQuery.isFetched &&
          alertsList?.length === 0 && (
          <div className={styles.noDataInfo}>
            <div className={styles.title}>
              {t("alerts_page.4")}
              <img
                src={
                  theme === "dark"
                    ? "/assets/alert_empty.svg"
                    : "/assets/alert_empty_black.svg"
                }
                alt=""
              />
            </div>
            <p className={styles.stepsLabel}>
              {t("others.52")}{" "}
              <img src="/assets/double_down_arrow.svg" alt="" />
            </p>
            <div className={styles.imgBlock}>
              <img
                src={
                  theme === "dark"
                    ? "/assets/alert_empty1.png"
                    : theme === "light"
                    ? "/assets/alert_empty1_light.png"
                    : "/assets/alert_empty1_brand.png"
                }
                alt=""
              />
              <img
                src={
                  theme === "dark"
                    ? "/assets/alert_empty2.png"
                    : theme === "light"
                    ? "/assets/alert_empty2_light.png"
                    : "/assets/alert_empty2_brand.png"
                }
                alt=""
              />
            </div>
            <span
              className={styles.link}
              onClick={() => history.push("/console/global_executive")}
            >
              {t("others.53")}
            </span>
          </div>
        )}
      </div>
      {totalAlerts > 0 && (
        <div className={styles.paginationWrapper}>
          <div className={styles.innerWrapper}>
            {
              <div className={styles.paginationBox}>
                <Button
                  disabled={startIndex === 0}
                  onClick={() => {
                    parseInt(alertsPageNumber, 10) === startIndex + 1 &&
                      setStartIndex((prev) => prev - 1);
                    parseInt(alertsPageNumber, 10) === startIndex + 1 &&
                      setAlertsPageNumber((prev) => parseInt(prev, 10) - 1);
                  }}
                >
                  <i className="fa fa-angle-left" />
                </Button>
                <span className={styles.pagination}>
                  <input
                    className={styles.pageIndex}
                    onKeyPress={handleUserPage}
                    type="text"
                    value={alertsPageNumber}
                    onChange={(e) => setAlertsPageNumber(e.target.value)}
                  />
                  {totalAlerts
                    ? ` / ${Math.ceil(totalAlerts / selectedPerPage)}`
                    : ""}
                </span>
                <Button
                  disabled={
                    alertsList?.length === 0 ||
                    (totalAlerts &&
                      startIndex + 1 ===
                        Math.ceil(totalAlerts / selectedPerPage)) ||
                    !alertsPageNumber
                  }
                  onClick={() => {
                    parseInt(alertsPageNumber, 10) === startIndex + 1 &&
                      setStartIndex((prev) => prev + 1);
                    parseInt(alertsPageNumber, 10) === startIndex + 1 &&
                      setAlertsPageNumber((prev) => parseInt(prev, 10) + 1);
                  }}
                >
                  <i className="fa fa-angle-right" />
                </Button>
              </div>
            }

            <Select
              className="review-page-number-selector"
              value={selectedPerPage + ` / ${t("label.10")}`}
              onChange={handlePageChange}
              style={{ backgroundColor: "transparent" }}
            >
              <Option value={10}>10</Option>
              <Option disabled={totalAlerts <= 10 ? true : false} value={25}>
                25
              </Option>
              <Option disabled={totalAlerts <= 25 ? true : false} value={50}>
                50
              </Option>
              <Option disabled={totalAlerts <= 50 ? true : false} value={100}>
                100
              </Option>
            </Select>
          </div>
          {totalAlerts > 0 && (
            <div className={styles.dashboardsTotal}>
              {selectedPerPage * startIndex +
                alertsList?.length +
                " of " +
                totalAlerts}
            </div>
          )}
        </div>
      )}
      {showEditAlert && (
        <AlertPopup
          theme={theme}
          handleAction={handleEditAlert}
          editCondition={selectedAlertCondition}
          editThreshold={selectedAlertThreshold}
          editUsers={selectedAlertUsers}
          editFrequency={selectedAlertFrequency}
          editSendSMS={sendSMSCheckbox}
          close={setShowEditAlert}
          type="edit"
          variant="anomaly"
          disable={disableBtn}
        />
      )}
      {showHistoryModal && historyRecord && (
        <AlertHistoryModal
          open={showHistoryModal}
          record={historyRecord}
          onClose={() => {
            setShowHistoryModal(false);
            setHistoryRecord(null);
          }}
          theme={theme}
        />
      )}
      <AlertLimitUpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limits={alertLimits}
        returnPath="/console/alerts"
        source="alerts_page"
      />
      {showAlertInfoPopup && (
        <div className={styles.modal}>
          <div className={styles.centerWrapper}>
            <div className={styles.alertContent}>
              <div className={styles.header}>
                <span>
                  <img
                    src={
                      theme === "dark"
                        ? "/assets/info1.svg"
                        : "/assets/info1_black.svg"
                    }
                    alt=""
                  />
                  {t("alerts_page.10")}
                </span>
                <img
                  src={
                    theme === "dark" ? "/assets/x.svg" : "/assets/x_black.svg"
                  }
                  onClick={() => setShowAlertInfoPopup(false)}
                  alt=""
                />
              </div>
              <div className={styles.belowContent}>
                <div className={styles.block}>
                  <span className={styles.label}>
                    <img
                      src={
                        theme === "dark"
                          ? "/assets/solid_line.svg"
                          : "/assets/solid_line_black.svg"
                      }
                      alt=""
                    />
                    {t("alerts_page.11")}
                  </span>
                  <span className={styles.info}>
                    {alertInfo?.alert_kpi?.join(", ") || alertInfo?.kpi_name}
                  </span>
                </div>
                <div className={styles.block}>
                  <span className={styles.label}>
                    <img
                      src={
                        theme === "dark"
                          ? "/assets/clock.svg"
                          : "/assets/clock_black.svg"
                      }
                      alt=""
                    />
                    {t("alerts_page.col_frequency")}
                  </span>
                  <span className={styles.info}>
                    {(alertInfo?.frequency ?? []).map((f) =>
                      formatFrequencyBadge(f, t)
                    ).join(", ")}
                  </span>
                </div>
                <div className={styles.block}>
                  <span className={styles.label}>
                    <img
                      src={
                        theme === "dark"
                          ? "/assets/alert_icon.svg"
                          : "/assets/alert_icon_black.svg"
                      }
                      alt=""
                    />
                    {t("alerts_page.col_last_alarm")}
                  </span>
                  <span className={styles.info}>
                    {alertInfo?.last_fired_at
                      ? formatAbsoluteDate(alertInfo.last_fired_at)
                      : t("alerts_page.never_fired")}
                    {alertInfo?.last_severity && (
                      <>
                        {" "}
                        <AlertSeverityBadge severity={alertInfo.last_severity} />
                      </>
                    )}
                  </span>
                </div>
                <div className={styles.underlineBlock}>
                  <span className={styles.label}>
                    <img
                      src={
                        theme === "dark"
                          ? "/assets/users.svg"
                          : "/assets/users_black.svg"
                      }
                      alt=""
                    />
                    {t("alerts_page.13")}
                  </span>
                  <div className={styles.wrapper}>
                    {alertInfo?.send_to?.map((user) => (
                      <span key={user}>
                        {user}
                        <img
                          src={
                            theme === "dark"
                              ? "/assets/copy.svg"
                              : "/assets/copy_black.svg"
                          }
                          alt=""
                          onClick={() => handleCopy(user)}
                        />
                      </span>
                    ))}
                  </div>
                </div>
                <div className={styles.block}>
                  <span className={styles.label}>
                    <img
                      width={"18px"}
                      src={
                        theme === "dark"
                          ? "/assets/calendar.svg"
                          : "/assets/calendar_black.svg"
                      }
                      alt=""
                    />
                    {t("alerts_page.col_fire_count")}
                  </span>
                  <span className={styles.info}>
                    {alertInfo?.fire_count_30d > 0
                      ? t("alerts_page.fire_count_label", {
                          count: alertInfo.fire_count_30d,
                        })
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDeleteAlert && (
        <div className={styles.modal}>
          <div className={styles.centerWrapper}>
            <div className={styles.deleteContent}>
              <p>{t("alerts_page.15")}</p>
              <div className={styles.block}>
                <button onClick={() => setShowDeleteAlert(false)}>
                  {t("button.3")}
                </button>
                <button disabled={disableBtn} onClick={handleDeleteAlert}>
                  {t("button.33")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
